(() => {
  const PLUS_ICON = `
    <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-plus-toggle icon--medium icon--thick" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path class="horizontal" d="M3.125 10H16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path class="vertical" d="M10 3.125V16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const CARET_LEFT = `
    <svg class="icon icon-caret-left icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const CARET_RIGHT = `
    <svg class="icon icon-caret-right icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const escape = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  function parseMaybeJson(value) {
    if (value == null || value === '') return '';
    if (typeof value !== 'string') return String(value);
    const trimmed = value.trim();
    if (!(trimmed.startsWith('[') || trimmed.startsWith('{'))) return value;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
              return item.label || item.name || item.value || JSON.stringify(item);
            }
            return String(item);
          })
          .filter(Boolean)
          .join(', ');
      }
      if (parsed && typeof parsed === 'object') {
        return parsed.label || parsed.name || parsed.value || value;
      }
    } catch (e) {
      /* keep raw */
    }
    return value;
  }

  function metaobjectField(metaobject, key) {
    const fields = metaobject?.fields || [];
    return fields.find((field) => field.key === key);
  }

  function guideFromMetaobject(node) {
    if (!node) return null;
    const titleField = metaobjectField(node, 'title');
    const imageField = metaobjectField(node, 'image');
    const title = titleField?.value || '';
    const imageUrl = imageField?.reference?.image?.url || '';
    const url = node.onlineStoreUrl || (node.handle ? `/pages/${node.handle}` : '#');
    return {
      title,
      imageUrl,
      url,
      handle: node.handle || '',
    };
  }

  function guidesFromMetafield(metafield) {
    const nodes = metafield?.references?.nodes || [];
    return nodes.map(guideFromMetaobject).filter((guide) => guide && (guide.title || guide.imageUrl));
  }

  function brandName(product) {
    return product.brand?.reference?.fields
      ? metaobjectField(product.brand.reference, 'name')?.value
      : product.brand?.reference?.field?.value || '';
  }

  function resolveBrand(product) {
    const ref = product.brand?.reference;
    if (!ref) return '';
    if (ref.field?.value) return ref.field.value;
    return metaobjectField(ref, 'name')?.value || '';
  }

  function metaobjectLabel(metaobject) {
    if (!metaobject) return '';
    const preferred = ['label', 'name', 'title', 'value'];
    for (const key of preferred) {
      const field = metaobjectField(metaobject, key);
      if (field?.value) return String(field.value).trim();
    }
    const first = metaobject.fields?.find((field) => field?.value);
    return first?.value ? String(first.value).trim() : '';
  }

  // Resolve taxonomy / metaobject metafields (e.g. shopify.country-of-origin)
  function resolveMetafieldLabel(metafield) {
    if (!metafield) return '';
    const single = metaobjectLabel(metafield.reference);
    if (single) return single;

    const list = (metafield.references?.nodes || [])
      .map(metaobjectLabel)
      .filter(Boolean);
    if (list.length) return list.join(', ');

    const raw = metafield.value;
    if (raw == null || raw === '') return '';
    if (String(raw).startsWith('gid://')) return '';
    return parseMaybeJson(raw);
  }

  function getGuidePanels(slot) {
    return [...slot.querySelectorAll('[data-guide-for]')];
  }

  function getOpenAccordion(root) {
    return [...root.querySelectorAll('.bundle-product-specs__item')].find(
      (item) => item.hasAttribute('open') || item.getAttribute('aria-expanded') === 'true'
    );
  }

  function openAccordionForProduct(root, productId) {
    if (!productId) return;
    const items = [...root.querySelectorAll('.bundle-product-specs__item')];
    const target = items.find((item) => String(item.dataset.productId) === String(productId));
    if (!target) return;

    const alreadyOpen =
      target.hasAttribute('open') || target.getAttribute('aria-expanded') === 'true';
    if (alreadyOpen) return;

    // AccordionGroup setter closes siblings via handleAfterToggle
    target.open = true;
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      /* ignore */
    }
  }

  function updateGuideNav(root) {
    const slot = root.querySelector('[data-bundle-guide-slot]');
    if (!slot) return;
    const nav = slot.querySelector('[data-bundle-guide-nav]');
    if (!nav) return;
    const panels = getGuidePanels(slot);
    nav.hidden = panels.length <= 1;
  }

  function showGuideAt(root, index, { syncAccordion = false } = {}) {
    const slot = root.querySelector('[data-bundle-guide-slot]');
    if (!slot) return;

    const panels = getGuidePanels(slot);
    if (!panels.length) {
      slot.hidden = true;
      return;
    }

    const nextIndex = ((index % panels.length) + panels.length) % panels.length;
    panels.forEach((panel, idx) => {
      panel.hidden = idx !== nextIndex;
    });

    const active = panels[nextIndex];
    root._bundleGuideIndex = nextIndex;
    root._bundleLastGuideProductId = String(active.dataset.guideFor || '');
    slot.hidden = false;
    updateGuideNav(root);

    if (syncAccordion) {
      openAccordionForProduct(root, active.dataset.guideFor);
    }
  }

  function syncGuideFromAccordion(root) {
    const slot = root.querySelector('[data-bundle-guide-slot]');
    if (!slot) return;

    const panels = getGuidePanels(slot);
    if (!panels.length) {
      slot.hidden = true;
      return;
    }

    const openItem = getOpenAccordion(root);
    if (openItem?.dataset.productId) {
      root._bundleLastGuideProductId = String(openItem.dataset.productId);
    }

    const productId = openItem?.dataset.productId
      ? String(openItem.dataset.productId)
      : root._bundleLastGuideProductId || '';

    const matchIndex = panels.findIndex(
      (panel) => productId !== '' && String(panel.dataset.guideFor) === productId
    );

    if (matchIndex >= 0) {
      showGuideAt(root, matchIndex, { syncAccordion: false });
      return;
    }

    showGuideAt(root, root._bundleGuideIndex || 0, { syncAccordion: false });
  }

  function bindGuideSync(root) {
    if (root._bundleGuideObserver) {
      root._bundleGuideObserver.disconnect();
      root._bundleGuideObserver = null;
    }

    const scheduleSync = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => syncGuideFromAccordion(root));
      });
    };

    if (!root._bundleGuideEventsBound) {
      root._bundleGuideEventsBound = true;

      root.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;
        if (!details.classList.contains('bundle-product-specs__item')) return;
        scheduleSync();
      });

      root.addEventListener('click', (event) => {
        if (!event.target.closest('.bundle-product-specs__summary')) return;
        scheduleSync();
        window.setTimeout(scheduleSync, 50);
        window.setTimeout(scheduleSync, 300);
      });

      root.addEventListener('click', (event) => {
        const prev = event.target.closest('[data-bundle-guide-prev]');
        const next = event.target.closest('[data-bundle-guide-next]');
        if (!prev && !next) return;

        event.preventDefault();
        const current = Number.isInteger(root._bundleGuideIndex)
          ? root._bundleGuideIndex
          : 0;
        const delta = prev ? -1 : 1;
        showGuideAt(root, current + delta, { syncAccordion: true });
      });
    }

    const observer = new MutationObserver(scheduleSync);
    root._bundleGuideObserver = observer;
    root.querySelectorAll('.bundle-product-specs__item').forEach((item) => {
      observer.observe(item, { attributes: true, attributeFilter: ['open', 'aria-expanded'] });
    });

    syncGuideFromAccordion(root);
  }

  function initSpecsRoot(root) {
    if (!root) return;
    bindGuideSync(root);
  }

  function initAll() {
    document.querySelectorAll('[data-bundle-product-specs]').forEach(initSpecsRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  function renderGuideNav() {
    return `
      <div class="bundle-product-specs__guide-nav" data-bundle-guide-nav hidden>
        <button type="button" class="bundle-product-specs__guide-btn bundle-product-specs__guide-btn--prev" data-bundle-guide-prev aria-label="Previous cooking guide">
          ${CARET_LEFT}
        </button>
        <button type="button" class="bundle-product-specs__guide-btn bundle-product-specs__guide-btn--next" data-bundle-guide-next aria-label="Next cooking guide">
          ${CARET_RIGHT}
        </button>
      </div>
    `;
  }

  function renderEmptyGuidePanel(productId, active, browseUrl, browseLabel, emptyImageUrl) {
    const image = emptyImageUrl
      ? `<img class="bundle-product-specs__guide-image" src="${escape(
          emptyImageUrl
        )}" alt="${escape(browseLabel)}" loading="lazy" width="800" height="800">`
      : `<div class="bundle-product-specs__guide-image bundle-product-specs__guide-image--placeholder" aria-hidden="true"></div>`;

    return `
      <div class="bundle-product-specs__guide-panel bundle-product-specs__guide-panel--empty" data-guide-for="${escape(
        productId
      )}" data-guide-empty ${active ? '' : 'hidden'}>
        <div class="bundle-product-specs__guide-empty">
          ${image}
          <a href="${escape(browseUrl)}" class="bundle-product-specs__guide-browse">${escape(
            browseLabel
          )}</a>
        </div>
      </div>
    `;
  }

  function renderGuidePanel(guide, productId, active, browseUrl, browseLabel, emptyImageUrl) {
    if (!guide) {
      return renderEmptyGuidePanel(productId, active, browseUrl, browseLabel, emptyImageUrl);
    }

    const image = guide.imageUrl
      ? `<img class="bundle-product-specs__guide-image" src="${escape(
          guide.imageUrl
        )}" alt="${escape(guide.title)}" loading="lazy" width="800" height="800">`
      : guide.title
        ? `<div class="bundle-product-specs__guide-image bundle-product-specs__guide-image--placeholder" aria-hidden="true"></div>`
        : '';
    const title = guide.title
      ? `<h3 class="bundle-product-specs__guide-title">Cooking Guide: ${escape(
          guide.title
        )}</h3>`
      : '';

    if (!image && !title) {
      return renderEmptyGuidePanel(productId, active, browseUrl, browseLabel, emptyImageUrl);
    }

    return `
      <div class="bundle-product-specs__guide-panel" data-guide-for="${escape(
        productId
      )}" ${active ? '' : 'hidden'}>
        <a href="${escape(guide.url || '#')}" class="bundle-product-specs__guide-link">
          ${image}
          ${title}
        </a>
      </div>
    `;
  }

  function pickAssignedGuide(productGuides) {
    return productGuides?.[0] || null;
  }

  function buildSpecRows(product) {
    const brand = resolveBrand(product) || brandName(product);
    const rows = [
      ['Grade / Program', parseMaybeJson(product.grade?.value)],
      ['Product Attribute', parseMaybeJson(product.specialty?.value)],
      ['Brand', brand],
      ['Country of Origin', resolveMetafieldLabel(product.country)],
      ['Aging Technique', parseMaybeJson(product.aging?.value)],
      ['Frozen', parseMaybeJson(product.frozen?.value)],
      ['Packaging', parseMaybeJson(product.packaging?.value)],
      ['Cut Type', parseMaybeJson(product.cut?.value)],
      [
        'Cut Spec',
        parseMaybeJson(product.cutSpec?.value) || parseMaybeJson(product.spec?.value),
      ],
      ['Ingredients', parseMaybeJson(product.ingredients?.value)],
      [
        'Allergens',
        resolveMetafieldLabel(product.allergensShopify) ||
          parseMaybeJson(product.allergensCustom?.value),
      ],
    ];

    return rows
      .filter(([, value]) => value)
      .map(
        ([label, value]) =>
          `<tr><th scope="row">${escape(label)}</th><td>${escape(value)}</td></tr>`
      )
      .join('');
  }

  // --- Storefront API fallback (Liquid cannot read Bundles components) ---
  const storefrontRoot = document.querySelector('[data-bundle-specs-storefront]');
  if (!storefrontRoot) return;

  const token = storefrontRoot.dataset.storefrontToken;
  const shop = storefrontRoot.dataset.shop;
  const handle = storefrontRoot.dataset.productHandle;
  const variantId = storefrontRoot.dataset.variantId;
  const heading = storefrontRoot.dataset.heading || 'Product Specs';
  const openFirst = storefrontRoot.dataset.openFirst !== 'false';
  const browseUrl = storefrontRoot.dataset.browseGuidesUrl || '/pages/all-cooking-guides';
  const browseLabel = storefrontRoot.dataset.browseGuidesLabel || 'Browse Cooking Guides';
  const emptyImageUrl =
    storefrontRoot.dataset.emptyGuideImage ||
    'https://cdn.shopify.com/s/files/1/0733/4071/5165/files/steak-and-flames-on-the-grill.jpg?width=800';

  if (!token || !shop || !handle) return;

  const query = `
    query BundleSpecs($handle: String!) {
      product(handle: $handle) {
        variants(first: 25) {
          nodes {
            id
            components(first: 25) {
              nodes {
                quantity
                productVariant {
                  product {
                    id
                    title
                    handle
                    onlineStoreUrl
                    grade: metafield(namespace: "custom", key: "grade_program") { value }
                    specialty: metafield(namespace: "custom", key: "specialty") { value }
                    aging: metafield(namespace: "custom", key: "aging_technique") { value }
                    frozen: metafield(namespace: "custom", key: "frozen") { value }
                    packaging: metafield(namespace: "custom", key: "portion_size") { value }
                    cut: metafield(namespace: "custom", key: "cut") { value }
                    cutSpec: metafield(namespace: "custom", key: "cut_spec") { value }
                    spec: metafield(namespace: "custom", key: "spec") { value }
                    ingredients: metafield(namespace: "custom", key: "ingredients") { value }
                    allergensCustom: metafield(namespace: "custom", key: "product_allergens") { value }
                    allergensShopify: metafield(namespace: "shopify", key: "allergen-information") {
                      value
                      reference {
                        ... on Metaobject {
                          fields { key value }
                        }
                      }
                      references(first: 10) {
                        nodes {
                          ... on Metaobject {
                            fields { key value }
                          }
                        }
                      }
                    }
                    country: metafield(namespace: "shopify", key: "country-of-origin") {
                      value
                      reference {
                        ... on Metaobject {
                          fields { key value }
                        }
                      }
                      references(first: 5) {
                        nodes {
                          ... on Metaobject {
                            fields { key value }
                          }
                        }
                      }
                    }
                    brand: metafield(namespace: "product", key: "brand") {
                      reference {
                        ... on Metaobject {
                          fields { key value }
                        }
                      }
                    }
                    guides: metafield(namespace: "custom", key: "related_cooking_guides") {
                      references(first: 5) {
                        nodes {
                          ... on Metaobject {
                            handle
                            onlineStoreUrl
                            fields {
                              key
                              value
                              reference {
                                ... on MediaImage { image { url altText } }
                              }
                            }
                          }
                        }
                      }
                    }
                    nutrition: metafield(namespace: "custom", key: "nutrition_label") {
                      reference {
                        ... on MediaImage {
                          image { url altText }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  fetch(`https://${shop}/api/2025-01/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    },
    body: JSON.stringify({ query, variables: { handle } }),
  })
    .then((res) => res.json())
    .then((payload) => {
      if (payload.errors?.length) {
        throw new Error(payload.errors[0].message);
      }

      const productData = payload.data?.product;
      const variants = productData?.variants?.nodes || [];
      const matched =
        variants.find((v) => String(v.id).endsWith(`/${variantId}`)) || variants[0];
      const nodes = matched?.components?.nodes || [];

      if (!nodes.length) {
        storefrontRoot.innerHTML =
          '<div class="page-width"><p class="text-subtext">No product specs available.</p></div>';
        return;
      }

      const guidePanels = [];

      const accordions = nodes
        .map((node, index) => {
          const product = node.productVariant?.product || {};
          const qty = node.quantity || 1;
          const productId = String(product.id || '').split('/').pop() || String(index);
          const nutritionUrl = product.nutrition?.reference?.image?.url;
          const nutritionId = `BundleNutrition-SF-${productId}`;
          const rows = buildSpecRows(product);
          const isActive = openFirst && index === 0;

          const productGuides = guidesFromMetafield(product.guides);
          const guide = pickAssignedGuide(productGuides);
          guidePanels.push(
            renderGuidePanel(guide, productId, isActive, browseUrl, browseLabel, emptyImageUrl)
          );

          const nutritionLink = nutritionUrl
            ? `<a href="#${nutritionId}" class="bundle-specs-table__nutrition" data-bundle-nutrition-open="${nutritionId}">
                <span class="bundle-specs-table__nutrition-icon" aria-hidden="true">
                  <img src="https://cdn.shopify.com/s/files/1/0733/4071/5165/files/nutritional-icon.png?v=1773783298&width=80" alt="" width="40" height="50" loading="lazy">
                </span>
                <span>View Nutritional Information Chart</span>
              </a>
              <dialog id="${nutritionId}" class="bundle-nutrition-modal">
                <button type="button" class="bundle-nutrition-modal__scrim" aria-label="Close" data-bundle-nutrition-close></button>
                <div class="bundle-nutrition-modal__panel">
                  <button type="button" class="bundle-nutrition-modal__dismiss" aria-label="Close" data-bundle-nutrition-close>×</button>
                  <img class="bundle-nutrition-modal__image" src="${escape(nutritionUrl)}" alt="${escape(
                    product.title
                  )}" loading="lazy">
                </div>
              </dialog>`
            : '';

          return `
            <details
              class="bundle-product-specs__item accordion-details"
              is="accordion-group"
              data-product-id="${escape(productId)}"
              ${isActive ? 'open aria-expanded="true"' : 'aria-expanded="false"'}
            >
              <summary class="bundle-product-specs__summary accordion-details__summary flex items-center justify-between gap-2">
                <span class="bundle-product-specs__title-row flex items-center gap-3">
                  <h3 class="bundle-product-specs__item-title">${escape(product.title)}</h3>
                  <span class="bundle-included-card__badge bundle-product-specs__qty" aria-label="${qty} included">${qty}</span>
                </span>
                ${PLUS_ICON}
              </summary>
              <div class="bundle-product-specs__panel accordion-details__content">
                <div class="bundle-specs-table">
                  <table class="bundle-specs-table__table"><tbody>${rows}</tbody></table>
                  ${nutritionLink}
                </div>
              </div>
            </details>
          `;
        })
        .join('');

      storefrontRoot.innerHTML = `
        <div class="page-width">
          <div class="bundle-product-specs__inner">
            <h2 class="bundle-product-specs__heading">${escape(heading)}</h2>
            <div class="bundle-product-specs__grid">
              <div class="bundle-product-specs__accordions accordion-parent">${accordions}</div>
              <aside class="bundle-product-specs__guide" data-bundle-guide-slot aria-live="polite">
                <div class="bundle-product-specs__guide-stage">
                  <div class="bundle-product-specs__guide-panels">
                    ${guidePanels.join('')}
                  </div>
                  ${renderGuideNav()}
                </div>
              </aside>
            </div>
          </div>
        </div>
      `;

      storefrontRoot.querySelectorAll('details[is="accordion-group"]').forEach((el) => {
        customElements.upgrade(el);
      });

      initSpecsRoot(storefrontRoot);
    })
    .catch((error) => {
      console.error('[bundle-product-specs]', error);
      storefrontRoot.innerHTML =
        '<div class="page-width"><p class="text-subtext">Could not load product specs.</p></div>';
    });
})();
